import { useState, useEffect, useCallback } from 'react';
import { ref, push, set, onValue, off, remove } from 'firebase/database';
import { database } from '@/firebase';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { GALLERY_MAX_SNAPSHOTS } from '@utils/validation';
import { saveCanvas, loadCanvas, listSavedCanvases, deleteSavedCanvas } from '@utils/canvasSaveLoad';
import { X, Image, Trash2, Download, Save, Upload, FolderOpen, Camera } from 'lucide-react';

export default function GalleryPanel({ stageRef, onClose }) {
  const { state, dispatch } = useApp();
  const { room, user, canvas } = state;
  const firebase = useFirebase();
  const [tab, setTab] = useState('snapshots');
  const [snapshots, setSnapshots] = useState([]);
  const [savedCanvases, setSavedCanvases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingCanvas, setSavingCanvas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  // Listen to gallery snapshots
  useEffect(() => {
    if (!room.code) return;
    const galleryRef = ref(database, `gallery/${room.code}`);
    const unsub = onValue(galleryRef, (snap) => {
      const data = snap.val();
      if (!data) { setSnapshots([]); return; }
      const list = Object.entries(data)
        .map(([id, val]) => ({ id, ...val }))
        .sort((a, b) => b.timestamp - a.timestamp);
      setSnapshots(list);
    });
    return () => off(galleryRef, 'value', unsub);
  }, [room.code]);

  // Load saved canvases list when tab is active
  useEffect(() => {
    if (tab !== 'saved' || !room.code) return;
    listSavedCanvases(room.code).then(setSavedCanvases);
  }, [tab, room.code]);

  // Save snapshot (existing behaviour — flat JPEG image)
  const handleSaveSnapshot = useCallback(async () => {
    const stage = stageRef.current;
    if (!stage || !room.code) return;
    if (snapshots.length >= GALLERY_MAX_SNAPSHOTS) return;

    setSaving(true);
    try {
      const dataUrl = stage.toDataURL({ pixelRatio: 0.5, mimeType: 'image/jpeg', quality: 0.7 });
      const snapshotRef = push(ref(database, `gallery/${room.code}`));
      await set(snapshotRef, {
        imageData: dataUrl,
        createdBy: user.nickname,
        userId: user.id,
        timestamp: Date.now(),
      });
    } catch (err) {
      console.error('Failed to save snapshot:', err);
    } finally {
      setSaving(false);
    }
  }, [stageRef, room.code, user.nickname, user.id, snapshots.length]);

  // Save canvas (strokes as JSON — resumable)
  const handleSaveCanvas = useCallback(async () => {
    if (!room.code || (canvas.allStrokes || []).length === 0) {
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Nothing to save — canvas is empty' } });
      return;
    }

    setSavingCanvas(true);
    const result = await saveCanvas({
      roomCode: room.code,
      strokes: canvas.allStrokes,
      background: canvas.background,
      userId: user.id,
      nickname: user.nickname,
      stageRef,
    });
    setSavingCanvas(false);

    if (result.success) {
      dispatch({ type: 'ADD_TOAST', payload: { message: 'Canvas saved — you can load it back anytime' } });
      // Refresh the list
      listSavedCanvases(room.code).then(setSavedCanvases);
    } else {
      dispatch({ type: 'ADD_TOAST', payload: { message: result.error || 'Failed to save canvas' } });
    }
  }, [room.code, canvas.allStrokes, canvas.background, user.id, user.nickname, stageRef, dispatch]);

  // Load a saved canvas
  const handleLoadCanvas = useCallback(async (save) => {
    if (!save.jsonUrl) return;

    setLoading(true);
    const result = await loadCanvas(save.jsonUrl);
    setLoading(false);

    if (!result.success) {
      dispatch({ type: 'ADD_TOAST', payload: { message: result.error || 'Failed to load canvas' } });
      return;
    }

    const { data } = result;

    // Clear current canvas (RTDB + local)
    try {
      await firebase.clearAllStrokes();
    } catch { /* ignore */ }
    dispatch({ type: 'CLEAR_ALL_STROKES' });

    // Set background if saved
    if (data.background) {
      dispatch({ type: 'SET_BACKGROUND', payload: data.background });
    }

    // Restore strokes to RTDB (other clients will pick them up via listeners)
    if (data.strokes?.length > 0) {
      await firebase.restoreStrokes(data.strokes);
    }

    dispatch({ type: 'ADD_TOAST', payload: { message: `Loaded "${save.name}" — ${data.strokeCount || data.strokes?.length || 0} strokes restored` } });
    onClose();
  }, [firebase, dispatch, onClose]);

  // Delete a saved canvas
  const handleDeleteSave = useCallback(async (save) => {
    await deleteSavedCanvas(room.code, save.id, save.storagePath);
    setSavedCanvases((prev) => prev.filter((s) => s.id !== save.id));
  }, [room.code]);

  // Delete a snapshot
  const handleDeleteSnapshot = useCallback(async (snapshotId) => {
    if (!room.code) return;
    await remove(ref(database, `gallery/${room.code}/${snapshotId}`));
  }, [room.code]);

  // Download a snapshot
  const handleDownload = useCallback((dataUrl, timestamp) => {
    const link = document.createElement('a');
    link.download = `drawlab-${room.code}-${timestamp}.jpg`;
    link.href = dataUrl;
    link.click();
  }, [room.code]);

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const hasStrokes = (canvas.allStrokes || []).length > 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl mx-4 overflow-hidden max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-charcoal font-display">Gallery</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setTab('snapshots')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-display transition-colors ${
                tab === 'snapshots' ? 'bg-white text-charcoal font-medium shadow-sm' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Camera className="w-3.5 h-3.5" />
              Snapshots
            </button>
            <button
              onClick={() => setTab('saved')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-display transition-colors ${
                tab === 'saved' ? 'bg-white text-charcoal font-medium shadow-sm' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <FolderOpen className="w-3.5 h-3.5" />
              Saved Canvases
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'snapshots' ? (
            <>
              {/* Save snapshot button */}
              <div className="mb-3">
                <button
                  onClick={handleSaveSnapshot}
                  disabled={saving || snapshots.length >= GALLERY_MAX_SNAPSHOTS}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-amber text-white text-sm font-display font-medium rounded-lg hover:bg-amber/90 transition-colors disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                  {snapshots.length >= GALLERY_MAX_SNAPSHOTS ? 'Gallery Full' : saving ? 'Saving...' : 'Save Snapshot'}
                </button>
              </div>

              {snapshots.length === 0 ? (
                <div className="text-center py-12 text-slate-400 font-display">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No snapshots yet</p>
                  <p className="text-xs mt-1">Snapshots are flat images — great for sharing</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {snapshots.map((snap) => (
                    <div
                      key={snap.id}
                      className="group relative rounded-xl overflow-hidden border border-slate-100 hover:border-amber/30 transition-colors cursor-pointer"
                      onClick={() => setPreview(snap)}
                    >
                      <img
                        src={snap.imageData}
                        alt={`Snapshot by ${snap.createdBy}`}
                        className="w-full aspect-[4/3] object-cover bg-canvas"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs font-display truncate">{snap.createdBy}</p>
                        <p className="text-white/60 text-[10px] font-mono">{formatTime(snap.timestamp)}</p>
                      </div>
                      <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(snap.imageData, snap.timestamp); }}
                          className="w-7 h-7 flex items-center justify-center rounded bg-white/90 text-charcoal hover:bg-white transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {(snap.userId === user.id || user.isHost) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteSnapshot(snap.id); }}
                            className="w-7 h-7 flex items-center justify-center rounded bg-white/90 text-red-500 hover:bg-white transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Saved Canvases tab */
            <>
              {/* Save canvas button */}
              <div className="mb-3">
                <button
                  onClick={handleSaveCanvas}
                  disabled={savingCanvas || !hasStrokes}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-amber text-white text-sm font-display font-medium rounded-lg hover:bg-amber/90 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingCanvas ? 'Saving...' : hasStrokes ? 'Save Current Canvas' : 'Canvas is empty'}
                </button>
                <p className="text-xs text-slate-400 font-display text-center mt-1">
                  Saves all strokes — load them back later to keep drawing
                </p>
              </div>

              {savedCanvases.length === 0 ? (
                <div className="text-center py-12 text-slate-400 font-display">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No saved canvases yet</p>
                  <p className="text-xs mt-1">Save your work and come back to it anytime</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {savedCanvases.map((save) => (
                    <div
                      key={save.id}
                      className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-amber/30 transition-colors"
                    >
                      {/* Thumbnail */}
                      {save.thumbnailUrl ? (
                        <img
                          src={save.thumbnailUrl}
                          alt={save.name}
                          className="w-16 h-12 rounded-lg object-cover bg-canvas shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          <Image className="w-5 h-5 text-slate-300" />
                        </div>
                      )}

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-charcoal font-display truncate">{save.name}</p>
                        <p className="text-xs text-slate-400 font-display">
                          {save.strokeCount} stroke{save.strokeCount !== 1 ? 's' : ''} · {save.savedBy} · {formatDate(save.savedAt)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleLoadCanvas(save)}
                          disabled={loading}
                          title="Load this canvas"
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-amber hover:bg-amber/10 transition-colors disabled:opacity-50"
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                        {(save.savedByUserId === user.id || user.isHost) && (
                          <button
                            onClick={() => handleDeleteSave(save)}
                            title="Delete saved canvas"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Full-size preview (snapshots only) */}
        {preview && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur"
            onClick={() => setPreview(null)}
          >
            <div className="max-w-4xl max-h-[90vh] mx-4" onClick={(e) => e.stopPropagation()}>
              <img
                src={preview.imageData}
                alt="Full preview"
                className="w-full h-full object-contain rounded-xl"
              />
              <div className="flex items-center justify-between mt-3 px-1">
                <p className="text-white/80 text-sm font-display">
                  By {preview.createdBy} at {formatTime(preview.timestamp)}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownload(preview.imageData, preview.timestamp)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white text-sm rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Download className="w-4 h-4" /> Download
                  </button>
                  <button
                    onClick={() => setPreview(null)}
                    className="px-3 py-1.5 bg-white/10 text-white text-sm rounded-lg hover:bg-white/20 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
